import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Slide,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { Auth } from "aws-amplify";
import React from "react";
import { useTranslation } from "react-i18next";
import { TransitionProps } from "@mui/material/transitions";

type AuthMode = "signin" | "signup" | "confirm";

export interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  onAuthenticated?: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function AuthDialog({
  open,
  onClose,
  onAuthenticated,
}: AuthDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = React.useState<AuthMode>("signin");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  const resetMessages = () => {
    setError(null);
    setInfo(null);
  };

  const closeAndReset = () => {
    setMode("signin");
    setEmail("");
    setPassword("");
    setCode("");
    resetMessages();
    onClose();
  };

  const handleSignIn = async () => {
    resetMessages();
    if (!email || !password) {
      setError(t("AuthDialog.requiredFields"));
      return;
    }
    setIsSubmitting(true);
    try {
      await Auth.signIn(email.trim(), password);
      setInfo(t("AuthDialog.successSignIn"));
      onAuthenticated?.();
      closeAndReset();
    } catch (err) {
      handleError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    resetMessages();
    if (!email || !password) {
      setError(t("AuthDialog.requiredFields"));
      return;
    }
    setIsSubmitting(true);
    try {
      await Auth.signUp({
        username: email.trim(),
        password,
        attributes: { email: email.trim() },
      });
      setMode("confirm");
      setInfo(t("AuthDialog.successSignUp"));
    } catch (err) {
      handleError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    resetMessages();
    if (!email || !code) {
      setError(t("AuthDialog.requiredFields"));
      return;
    }
    setIsSubmitting(true);
    try {
      await Auth.confirmSignUp(email.trim(), code.trim());
      setInfo(t("AuthDialog.successConfirm"));
      setMode("signin");
    } catch (err) {
      handleError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    resetMessages();
    if (!email) {
      setError(t("AuthDialog.emailRequired"));
      return;
    }
    try {
      await Auth.resendSignUp(email.trim());
      setInfo(t("AuthDialog.codeResent"));
    } catch (err) {
      handleError(err);
    }
  };

  const handleError = (err: unknown) => {
    console.error("AuthDialog error", err);
    if (err instanceof Error) {
      setError(err.message);
    } else if (typeof err === "string") {
      setError(err);
    } else {
      setError(t("AuthDialog.genericError"));
    }
  };

  const getTitle = () => {
    if (mode === "signup") {
      return t("AuthDialog.signUpTitle");
    }
    if (mode === "confirm") {
      return t("AuthDialog.confirmTitle");
    }
    return t("AuthDialog.signInTitle");
  };

  const showPasswordField = mode === "signin" || mode === "signup";

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSubmitting) {
      if (mode === "signin") handleSignIn();
      else if (mode === "signup") handleSignUp();
      else if (mode === "confirm") handleConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pt: 4,
          pb: 1,
          fontSize: "1.75rem",
          fontWeight: 600,
        }}
      >
        {getTitle()}
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 3, pt: 4 }}>
        <Stack spacing={3} onKeyPress={handleKeyPress}>
          {error && (
            <Alert
              severity="error"
              sx={{ borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {info && (
            <Alert
              severity="success"
              sx={{ borderRadius: 2 }}
              icon={<CheckCircleOutline />}
              onClose={() => setInfo(null)}
            >
              {info}
            </Alert>
          )}

          <TextField
            label={t("AuthDialog.emailLabel")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            autoComplete="email"
            autoFocus={mode !== "confirm"}
            disabled={isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          {showPasswordField && (
            <TextField
              label={t("AuthDialog.passwordLabel")}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              disabled={isSubmitting}
              autoComplete={
                mode === "signup" ? "new-password" : "current-password"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          )}

          {mode === "confirm" && (
            <>
              <TextField
                label={t("AuthDialog.codeLabel")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                autoFocus
                disabled={isSubmitting}
                inputProps={{
                  style: { textAlign: "center", fontSize: "1.5rem" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <Button
                onClick={handleResend}
                variant="text"
                disabled={isSubmitting}
                sx={{ alignSelf: "center" }}
              >
                {t("AuthDialog.resendCode")}
              </Button>
            </>
          )}

          <Box sx={{ pt: 1 }}>
            {mode === "signin" && (
              <Button
                onClick={handleSignIn}
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
                startIcon={
                  isSubmitting && <CircularProgress size={20} color="inherit" />
                }
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? "..." : t("AuthDialog.signInCta")}
              </Button>
            )}
            {mode === "signup" && (
              <Button
                onClick={handleSignUp}
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
                startIcon={
                  isSubmitting && <CircularProgress size={20} color="inherit" />
                }
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? "..." : t("AuthDialog.signUpCta")}
              </Button>
            )}
            {mode === "confirm" && (
              <Button
                onClick={handleConfirm}
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
                startIcon={
                  isSubmitting && <CircularProgress size={20} color="inherit" />
                }
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? "..." : t("AuthDialog.confirmCta")}
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          justifyContent: "center",
          px: 4,
          py: 2,
          flexDirection: "column",
          gap: 1,
        }}
      >
        {mode === "signin" && (
          <Button
            onClick={() => setMode("signup")}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
          >
            {t("AuthDialog.switchToSignUp")}
          </Button>
        )}
        {(mode === "signup" || mode === "confirm") && (
          <Button
            onClick={() => setMode("signin")}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
          >
            {t("AuthDialog.switchToSignIn")}
          </Button>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 400, px: 2 }}
        >
          {t("AuthDialog.privacyHint")}
        </Typography>
      </DialogActions>
    </Dialog>
  );
}
